import type { Dictionary } from './en.ts';

export const nl: Dictionary = {
  common: {
    tagline: 'realtime spraakvertaler',
    interfaceLanguage: 'Taal van de interface',
  },

  login: {
    title: 'Inloggen',
    lede: 'Voer de toegangscode voor de demo in. Deze browser onthoudt hem 12 uur.',
    submit: 'Inloggen',
    checking: 'Controleren…',
    wrong: 'Die code klopt niet. Controleer hem en probeer het opnieuw.',
    failed: 'Inloggen lukte niet. Probeer het zo opnieuw.',
  },

  home: {
    title: 'Een gesprek in twee talen',
    lede: 'Jullie spreken allebei je eigen taal en horen de ander vertaald. Twee mensen per kamer, zonder aanmelden.',
    myLanguage: 'Mijn taal',
    peerLanguage: 'Hun taal',
    accessCode: 'Toegangscode voor de demo',
    signOut: 'Uitloggen',
    samePair: 'Kies twee verschillende talen — anders valt er niets te vertalen.',
    create: 'Kamer aanmaken',
    creating: 'Bezig met aanmaken…',
    createFailed: 'De kamer kon niet worden aangemaakt',

    readyTitle: 'De kamer is klaar',
    youHear: (language: string) => `Jij hoort: ${language}.`,
    theyHear: (language: string) => `Zij horen: ${language}.`,
    inviteLabel: 'Uitnodigingslink — stuur die naar de ander',
    copy: 'Kopiëren',
    copied: 'Gekopieerd',
    inviteNote:
      'De link is 15 minuten geldig en laat precies één persoon binnen. Zet hem nergens openbaar — hij is de sleutel tot de kamer.',
    sourceSubtitles: 'Toon ook de oorspronkelijke woorden',
    sourceSubtitlesOn:
      'Je ziet naast de vertaling wat iemand werkelijk zei, in de eigen taal. De aanbieder rekent dit apart, dus het gesprek wordt duurder.',
    sourceSubtitlesOff:
      'Alleen de vertaling wordt getoond. Er wordt niets in de oorspronkelijke taal uitgeschreven en er komen geen extra kosten bij.',
    sourceSubtitlesReady: 'De oorspronkelijke woorden worden ook getoond.',
    goToCheck: 'Mijn geluid controleren',
  },

  voice: {
    you: 'Jij',
    peer: 'De ander',
    language: 'Taal',
    provider: 'Voice-over',
    openaiNote: 'ingebouwde stem, klinkt meteen',
    elevenlabsNote: 'schonere stem, begint als een zin af is',
    noVoices: 'geen stemmen voor deze taal',
    voice: 'Stem',
    builtIn: 'Ingebouwde OpenAI-stem',
    loading: 'Stemmen laden…',
    failed: 'De stemmen konden niet worden geladen. Probeer het later opnieuw.',
    hintBuiltIn: 'Eén stem voor alle talen, zoals voorheen.',
    hintLibrary: 'Een stem uit de bibliotheek van ElevenLabs.',
    groupVerified: 'Gecontroleerd voor deze taal',
    groupOther: 'Andere stemmen',
    accent: 'Deze stem is niet gecontroleerd voor deze taal en kan met een accent klinken.',
    noSample: 'Deze stem heeft geen voorbeeld',
    listen: 'Beluisteren',
    stop: 'Stoppen',
    needVoice: 'Kies een stem voor elke kant met ElevenLabs, of zet die terug op OpenAI.',
  },

  join: {
    checking: 'De uitnodiging wordt gecontroleerd…',
    title: 'Uitnodiging voor een gesprek',
    youWillHear: (language: string) => `Jij gaat horen: ${language}.`,
    theyHear: (language: string) => `Zij horen: ${language}.`,
    connect: 'Deelnemen',
    connecting: 'Bezig met deelnemen…',
    full: 'De kamer zit vol.',
    failedTitle: 'Deelnemen lukte niet',
    createYourOwn: 'Maak je eigen kamer',
    noInvite: 'In deze link zit geen uitnodiging. Vraag er opnieuw om.',
    unavailable: 'Deze kamer is niet beschikbaar',
    joinFailed: 'Deelnemen mislukt',
  },

  check: {
    title: 'Geluidscontrole',
    lede: 'Zet een koptelefoon op: zonder koptelefoon gaat de vertaling terug je microfoon in en hoort de ander een echo.',
    microphone: 'Microfoon',
    defaultMic: 'Standaardmicrofoon',
    level: 'Niveau — zeg eens iets',
    applied: (features: string) => `Je browser paste toe: ${features}.`,
    appliedNone: 'geen bewerking',
    echoCancellation: 'echo-onderdrukking',
    noiseSuppression: 'ruisonderdrukking',
    autoGain: 'automatische versterking',
    measureNote: 'Meet met de koptelefoon op.',
    testSound: 'Geluid testen',
    testAgain: 'Geluid opnieuw testen',
    start: 'Klinkt goed, beginnen',
    starting: 'Verbinden…',
    micDenied:
      'Toegang tot de microfoon is geweigerd. Sta die toe in je browserinstellingen en laad de pagina opnieuw.',
    micNotFound: 'Geen microfoon gevonden. Sluit er een aan en laad de pagina opnieuw.',
    micFailed: 'De microfoon kon niet worden geopend.',
  },

  call: {
    title: 'Gesprek',
    timeLeft: (time: string) => `· nog ${time}`,
    waitingPeer: 'Wachten op de ander. Stuur hun de uitnodigingslink.',
    autoplayBlocked: 'Je browser blokkeerde het afspelen van geluid.',
    enableSound: 'Geluid aanzetten',
    lagging:
      'De vertaling loopt achter. Je kunt de vertaler herstarten — het onafgemaakte fragment gaat dan verloren.',
    translatorStopped: (reason: string) =>
      `De vertaling is gestopt: ${reason}. Het gesprek loopt door — herstart de vertaler.`,
    translationVolume: 'Volume van de vertaling',
    hearOriginal: 'De oorspronkelijke stem horen',
    muteMic: 'Microfoon uitzetten',
    unmuteMic: 'Microfoon aanzetten',
    restart: 'Vertaling herstarten',
    asleep:
      'Er is een minuut niets gezegd, dus de vertaling is gepauzeerd om kosten te besparen. Ze gaat vanzelf verder zodra je gesprekspartner spreekt.',
    wake: 'Nu hervatten',
    leave: 'Verlaten',
    peerMuted: 'De ander heeft de microfoon uitgezet',

    metricConnect: 'Opstarten van de vertaler',
    metricFirst: 'Eerste vertaling',
    metricLag: 'Geschatte vertraging',
    metricRtt: 'RTT naar de provider',
    metricVoiceDelay: 'Vertraging voice-over',
    metricCost: 'Kosten tot nu toe (schatting)',
    costBreakdown: (openai: string, voice: string, credits: number) =>
      `OpenAI ${openai} · ElevenLabs ${voice} · credits: ${credits}`,
    costNote: 'Gepubliceerde prijzen uit de serverinstellingen, niet de factuur van de aanbieders.',
    metricVoiceLead: 'ElevenLabs t.o.v. OpenAI, gemiddeld',
    voiceLeadEarlier: (seconds: string) => `${seconds} s eerder`,
    voiceLeadLater: (seconds: string) => `${seconds} s later`,
    iHear: (voice: string) => `Ik hoor: ${voice}`,
    peerHears: (voice: string) => `De ander hoort: ${voice}`,
    voiceFellBack: 'OpenAI (ElevenLabs reageerde niet)',

    endedTitle: 'Het gesprek is afgelopen',
    endedNewRoom: 'Maak voor een nieuw gesprek een nieuwe kamer aan.',
    newRoom: 'Nieuwe kamer',
    rateTitle: 'Beoordeel het gesprek',
  },

  closed: {
    'peer-left': 'De ander heeft het gesprek beëindigd.',
    'time-limit': 'De tijdslimiet van de demo is bereikt.',
    'peer-lost': 'De ander was te lang weg.',
    'server-shutdown': 'De server start opnieuw op.',
    expired: 'De kamer is verlopen.',
    unknown: 'De verbinding is gesloten.',
    signalLost: 'Geen contact meer met de server. Maak een nieuwe kamer aan.',
  },

  status: {
    serverConnecting: 'Server: verbinden',
    serverOpen: 'Server: verbonden',
    serverReconnecting: 'Server: opnieuw verbinden',
    serverClosed: 'Server: verbinding verbroken',

    peerWaiting: 'Zij: wachten',
    peerConnecting: 'Zij: verbinden',
    peerConnected: 'Zij: verbonden',
    peerReconnecting: 'Zij: herstellen',
    peerFailed: 'Zij: geen verbinding',
    peerGone: 'Zij: weg',

    translatorIdle: 'Vertaling voor mij: niet gestart',
    translatorConnecting: 'Vertaling voor mij: starten',
    translatorReady: 'Vertaling voor mij: actief',
    translatorReconnecting: 'Vertaling voor mij: herstellen',
    translatorFailed: 'Vertaling voor mij: mislukt',
    translatorClosed: 'Vertaling voor mij: gestopt',
    translatorAsleep: 'Vertaling voor mij: gepauzeerd',

    peerTranslatorReady: 'Vertaling voor de ander: actief',
    peerTranslatorWaiting: 'Vertaling voor de ander: wachten',
    peerTranslatorAsleep: 'Vertaling voor de ander: gepauzeerd',
    relayed: 'Route: via TURN',
  },

  subtitles: {
    them: 'Zij',
    you: 'Jij',
    themEmpty: 'Zij hebben nog niets gezegd',
    youEmpty: 'Zeg iets — je woorden en de vertaling ervan komen hier te staan',
  },

  rating: {
    accuracy: 'Nauwkeurigheid van de vertaling',
    voice: 'Hoe natuurlijk de stem klinkt',
    comfort: 'Hoe prettig het gesprek verliep',
    send: 'Beoordelingen versturen',
    thanks: 'Bedankt — de beoordelingen zijn opgeslagen.',
  },

  errors: {
    no_credit: 'het OpenAI-account heeft geen tegoed meer — vul het aan',
    rate_limited: 'de vertaaldienst beperkt ons aantal verzoeken — probeer het over een minuut',
    unconfigured: 'op de server is geen vertaalsleutel ingesteld',
    unreachable: 'de server kon de vertaaldienst niet bereiken',
    offline: 'de server is onbereikbaar — controleer je verbinding',
    session_expired: 'de vertaalsessie is verlopen — herstart de vertaling',
    room_closed: 'deze kamer is al gesloten',
    bad_offer: 'de browser maakte een ongeldig verbindingsvoorstel',
    provider_refused: (status: number) => `de vertaaldienst weigerde de verbinding (${status})`,
    unknown: 'de vertaling kon niet worden gestart',
  },

  banner: {
    providerUnconfigured:
      'Op de server is geen sleutel voor een vertaalprovider ingesteld. Je kunt wel een kamer aanmaken, maar er wordt niets vertaald.',
  },
};
